// Catalog CONFIG (structure only). Products come from the backend API.
// Category hierarchy reflects Jose's approved structure. Categories with no
// products yet simply show a "coming soon" state; none are fabricated.

export const CATEGORY_GROUPS = [
  {
    label: "Equipment",
    categories: [
      { slug: "air-conditioner-condensers", name: "Air Conditioner Condensers", icon: "Snowflake", blurb: "Outdoor AC condensing units." },
      { slug: "heat-pump-condensers", name: "Heat Pump Condensers", icon: "Thermometer", blurb: "Outdoor heat pump condensers." },
      { slug: "furnaces", name: "Furnaces", icon: "Flame", blurb: "Gas and electric furnaces." },
      { slug: "air-handlers", name: "Air Handlers", icon: "Fan", blurb: "Indoor air handling units." },
      { slug: "coils", name: "Coils", icon: "LayoutGrid", blurb: "Evaporator and condenser coils." },
    ],
  },
  {
    label: "Packaged Units",
    categories: [
      { slug: "gas-electric-packaged-units", name: "Gas / Electric Packaged Units", icon: "Package", blurb: "Self-contained gas/electric units." },
      { slug: "heat-pump-packaged-units", name: "Heat Pump Packaged Units", icon: "Package", blurb: "Self-contained packaged heat pumps." },
      { slug: "ac-only-packaged-units", name: "AC-Only Packaged Units", icon: "Package", blurb: "Self-contained cooling units.", hidden: true },
    ],
  },
  {
    label: "Ductless Mini-Split",
    categories: [
      { slug: "single-zone-systems", name: "Single Zone Systems", icon: "Thermometer", blurb: "Matched single-zone ductless systems." },
      { slug: "multi-zone-systems", name: "Multi Zone Systems", icon: "Layers", blurb: "Multi-zone ductless systems." },
    ],
  },
  {
    label: "VRF Systems",
    categories: [
      { slug: "mini-vrf-outdoor-units", name: "Mini VRF Outdoor Units", icon: "Wind", blurb: "Mini VRF outdoor condensers." },
      { slug: "mini-vrf-indoor-units", name: "Mini VRF Indoor Units", icon: "Snowflake", blurb: "Mini VRF indoor units & air handlers." },
    ],
  },
];

export const CATEGORIES = CATEGORY_GROUPS.flatMap((g) =>
  g.categories.map((c) => ({ ...c, group: g.label }))
);

export const VISIBLE_CATEGORIES = CATEGORIES.filter((c) => !c.hidden);

// Full brand registry (for slug -> name resolution). `active` brands currently
// have supplied inventory and are shown in navigation / brand listings. Flip a
// brand to active:true once products for it are added.
export const BRANDS = [
  { slug: "midea", name: "Midea", tagline: "Inverter comfort systems", active: true },
  { slug: "goodman", name: "Goodman", tagline: "Dependable, American-built HVAC", active: true },
  { slug: "daikin", name: "Daikin", tagline: "Global inverter technology", active: true },
  { slug: "tosot", name: "TOSOT", tagline: "Efficient ductless systems", active: true },
  { slug: "tcl", name: "TCL", tagline: "Smart WiFi mini-splits", active: true },
  { slug: "aspen", name: "Aspen", tagline: "Coils & air-side components", active: true },
  { slug: "lennox", name: "Lennox", tagline: "Premium home comfort", active: false },
  { slug: "adp", name: "ADP", tagline: "Coils & air handlers", active: false },
];

export const ACTIVE_BRANDS = BRANDS.filter((b) => b.active);

export const SPEC_LABELS = {
  type: "Product Type",
  tonnage: "Nominal Capacity",
  btu: "BTU Rating",
  refrigerant: "Refrigerant",
  seer2: "Efficiency (SEER2)",
  eer2: "Efficiency (EER2)",
  hspf2: "Efficiency (HSPF2)",
  voltage: "Voltage",
  phase: "Electrical Phase",
  discharge: "Airflow / Discharge",
  nox: "NOx Rating",
  zones: "Zones",
  staticPressure: "Static Pressure",
  series: "Series",
  lineSet: "Line Set",
  wifi: "WiFi Control",
  dimensions: "Dimensions",
  weight: "Weight",
  compatibility: "Compatibility",
};

export const getCategory = (slug) => CATEGORIES.find((c) => c.slug === slug);
export const getBrand = (slug) => BRANDS.find((b) => b.slug === slug);

// Heating-oriented categories get a warm (flame) accent; cooling/air-side get cool (ice).
const WARM_CATEGORIES = new Set(["gas-electric-packaged-units", "furnaces"]);
export const categoryAccent = (slug) => (WARM_CATEGORIES.has(slug) ? "warm" : "cool");

export function formatPrice(price) {
  if (price === null || price === undefined) return null;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);
}

export function resolveImage(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

// Returns { mode: "show" | "request", price, original }
export function displayPrice(p) {
  if (!p || p.priceVisibility !== "show") return { mode: "request" };
  if (p.clearance && p.salePrice != null) return { mode: "show", price: p.salePrice, original: p.price };
  if (p.price != null) return { mode: "show", price: p.price };
  return { mode: "request" };
}

export function productHighlights(p) {
  const s = p.specs || {};
  const parts = [];
  if (s.type) parts.push(s.type);
  if (s.btu) parts.push(`${Number(s.btu).toLocaleString()} BTU`);
  if (s.tonnage) parts.push(s.tonnage);
  if (s.refrigerant) parts.push(s.refrigerant);
  return parts.join(" · ");
}

// Compact technical chips for product cards (capacity / efficiency / power).
export function specChips(p) {
  const s = p.specs || {};
  const chips = [];
  if (s.tonnage) chips.push(s.tonnage);
  else if (s.btu) chips.push(`${Number(s.btu).toLocaleString()} BTU`);
  if (s.seer2) chips.push(`${s.seer2} SEER2`);
  if (s.voltage) chips.push(s.voltage);
  else if (s.discharge) chips.push(s.discharge);
  else if (s.zones) chips.push(s.zones);
  return chips;
}

// Client-side filter over a fetched product list.
export function filterProducts(list, { categories = [], brands = [], types = [], refrigerants = [], search = "", sort = "featured" } = {}) {
  const q = search.trim().toLowerCase();
  let out = list.filter((p) => {
    const cats = p.categories && p.categories.length ? p.categories : [p.categorySlug];
    const catOk = categories.length === 0 || categories.some((c) => cats.includes(c));
    const brandOk = brands.length === 0 || brands.includes(p.brandSlug);
    const typeOk = types.length === 0 || types.includes(p.specs?.type);
    const refOk = refrigerants.length === 0 || refrigerants.includes(p.specs?.refrigerant);
    const searchOk = !q || [p.name, p.model, getBrand(p.brandSlug)?.name, getCategory(p.categorySlug)?.name, p.specs?.type].filter(Boolean).some((s) => s.toLowerCase().includes(q));
    return catOk && brandOk && typeOk && refOk && searchOk;
  });
  const priceOf = (p) => {
    const dp = displayPrice(p);
    return dp.mode === "show" ? dp.price : Infinity;
  };
  if (sort === "price-asc") out = [...out].sort((a, b) => priceOf(a) - priceOf(b));
  else if (sort === "price-desc") out = [...out].sort((a, b) => (priceOf(b) === Infinity ? -1 : priceOf(b)) - (priceOf(a) === Infinity ? -1 : priceOf(a)));
  else if (sort === "name-asc") out = [...out].sort((a, b) => a.name.localeCompare(b.name));
  else if (sort === "relevance") return out;
  else out = [...out].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
  return out;
}

export function facetsFrom(list) {
  const types = [...new Set(list.map((p) => p.specs?.type).filter(Boolean))].sort();
  const refrigerants = [...new Set(list.map((p) => p.specs?.refrigerant).filter(Boolean))].sort();
  const brandSlugs = [...new Set(list.map((p) => p.brandSlug).filter(Boolean))];
  const brands = BRANDS.filter((b) => brandSlugs.includes(b.slug));
  return { types, refrigerants, brands };
}
